import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { DashboardMonthlyPaymentMethodCost } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { MONTH_LABELS, formatAxis, monthKey, plnTooltipFormatter } from './chart-utils';

interface CostsByPaymentChartProps {
  data: DashboardMonthlyPaymentMethodCost[];
  year: number;
}

const PAYMENT_ORDER = ['transfer', 'card', 'cash', 'other'] as const;

const PAYMENT_COLORS: Record<string, string> = {
  transfer: 'var(--chart-1)',
  card: 'var(--chart-5)',
  cash: 'var(--chart-2)',
  other: 'var(--muted-foreground)',
};

export function CostsByPaymentChart({ data, year }: CostsByPaymentChartProps) {
  const groupsPresent = new Set(data.map((d) => d.group));
  const groups = PAYMENT_ORDER.filter((g) => groupsPresent.has(g));

  const chartData = MONTH_LABELS.map((label, m) => {
    const row: Record<string, number | string> = { month: label };
    for (const group of groups) {
      const found = data.find((d) => d.month === monthKey(year, m) && d.group === group);
      row[group] = found?.totalGross ?? 0;
    }
    return row;
  });

  const config: ChartConfig = Object.fromEntries(
    groups.map((g) => [g, { label: t(`dashboard.paymentMethods.${g}`), color: PAYMENT_COLORS[g] }])
  );

  return (
    <ChartCard title={t('dashboard.monthlyPaymentMethodCosts')}>
      <ChartContainer config={config} className="h-[300px] w-full">
        <BarChart data={chartData}>
          <CartesianGrid vertical={false} strokeOpacity={0.4} />
          <XAxis dataKey="month" tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(label) => `${label} ${year}`}
                formatter={plnTooltipFormatter(config, { hideZero: true })}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          {groups.map((group) => (
            <Bar
              key={group}
              dataKey={group}
              stackId="payment"
              fill={`var(--color-${group})`}
              stroke="var(--card)"
              strokeWidth={1}
              maxBarSize={32}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
